<template>
  <div>
    <v-dialog v-model="importAuditedDialog" max-width="700" persistent scrollable>
      <v-card>
        <v-toolbar color="primary" dark flat dense>
          <v-toolbar-title class="text-body-1 font-weight-bold">
            {{ $t('carbonCredit.plotTreeMapping.imports.dialogTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="closeImportAuditedDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p class="body-2 mb-3">{{ $t('carbonCredit.plotTreeMapping.imports.templateNote') }}</p>
          <v-btn
            color="primary"
            class="px-2 mb-4"
            href="/files/template.csv"
          >
            {{ $t('carbonCredit.plotTreeMapping.imports.downloadTemplate') }}
            <v-icon right small class="ml-1">mdi-download</v-icon>
          </v-btn>
          <div
            class="dropzone-import"
            @dragenter.prevent="importAuditedDragging = true"
            @dragleave.prevent="importAuditedDragging = false"
          >
            <label
              v-if="!importAuditedFileName"
              class="dropzone-import__label d-flex flex-column align-center justify-center px-3 py-6"
              :class="{ 'dropzone-import__label--active': importAuditedDragging }"
              @drop.prevent="onImportAuditedDrop"
              @dragover.prevent
            >
              <v-icon large color="primary">mdi-file-upload-outline</v-icon>
              <input class="d-none" type="file" accept=".csv,.xls,.xlsx" @change="handleImportAuditedFileChange" />
              <span class="font-weight-medium pt-2 primary--text text-decoration-underline">
                {{ $t('carbonCredit.plotTreeMapping.imports.selectFile') }}
              </span>
              <span class="caption">{{ $t('carbonCredit.plotTreeMapping.imports.dropHere') }}</span>
            </label>
            <div v-else class="dropzone-import__selected pa-4 d-flex flex-column align-center">
              <v-icon color="primary">mdi-file-document-outline</v-icon>
              <span class="text-body-2 mt-2">{{ importAuditedFileName }}</span>
              <v-btn text small color="primary" class="mt-2" @click="clearImportAuditedFile">
                {{ $t('carbonCredit.plotTreeMapping.imports.removeFile') }}
              </v-btn>
            </div>
          </div>
          <p v-if="importAuditedFileError" class="error--text caption mt-2 mb-0">{{ importAuditedFileError }}</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn outlined color="primary" @click="closeImportAuditedDialog">
            {{ $t('carbonCredit.plotTreeMapping.imports.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!canSubmitAuditedImport"
            :loading="importAuditedSubmitting"
            @click="submitAuditedPlotImport"
          >
            {{ $t('carbonCredit.plotTreeMapping.imports.import') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="importAuditedResultDialog"
      width="800"
      persistent
      scrollable
      content-class="plot-import-result-dialog"
    >
      <v-card class="plot-import-result-card" rounded="0">
        <v-toolbar color="primary" dark flat>
          <v-toolbar-title class="font-weight-bold white--text">
            {{ $t('carbonCredit.plotTreeMapping.imports.resultDialogTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="closeImportResultDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card flat class="mb-2 mt-4">
          <span class="plot-import-result-muted pl-6">{{ $t('carbonCredit.plotTreeMapping.imports.importCompletedLabel') }}</span>
          <div class="text-center px-2">
            <v-row justify="center" align="center">
              <v-col cols="12" sm="11">
                <div class="d-flex align-center mt-2">
                  <div class="flex-grow-1 plot-import-result-progress">
                    <v-progress-linear
                      :value="importResultProgress"
                      :color="importResultProgressColor"
                      rounded
                      height="10"
                    />
                  </div>
                  <div class="pl-2 flex-shrink-0">
                    <small :class="importResultProgressColor + '--text font-weight-bold'">{{ importResultProgress }} %</small>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
          <span class="plot-import-result-muted pl-6 d-inline-block mt-2 mb-2">
            {{
              $t('carbonCredit.plotTreeMapping.imports.recordsImportedLine', {
                success: importResultSuccessCount,
                total: importResultTotalCount,
              })
            }}
          </span>
        </v-card>

        <v-card flat class="mb-2">
          <div class="text-center py-4 px-2">
            <v-row justify="center" align="center" class="plot-import-result-stats mb-4">
              <v-col cols="12" sm="4">
                <div>
                  <span class="plot-import-result-stat-num plot-import-result-stat-total">{{ importResultTotalCount }}</span>
                  <br>
                  <span class="plot-import-result-stat-label">{{ $t('carbonCredit.plotTreeMapping.imports.totalRecordsLabel') }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div>
                  <span class="plot-import-result-stat-num plot-import-result-stat-fail">{{ importResultFailCount }}</span>
                  <br>
                  <span class="plot-import-result-stat-label">{{ $t('carbonCredit.plotTreeMapping.imports.failedLabel') }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div>
                  <span class="plot-import-result-stat-num plot-import-result-stat-success">{{ importResultSuccessCount }}</span>
                  <br>
                  <span class="plot-import-result-stat-label">{{ $t('carbonCredit.plotTreeMapping.imports.successLabel') }}</span>
                </div>
              </v-col>
            </v-row>
            <div v-if="importResultSkippedCount > 0" class="caption grey--text text--darken-1 mb-2">
              {{ $t('carbonCredit.plotTreeMapping.imports.skippedLabel') }}: {{ importResultSkippedCount }}
            </div>

            <div v-if="importResultRows.length" class="pa-2">
              <v-data-table
                :headers="importResultHeaders"
                :items="importResultRows"
                item-key="rowKey"
                class="elevation-1 plot-import-result-table"
                hide-default-footer
                :items-per-page="-1"
                :header-props="{ sortIcon: 'mdi-menu-up' }"
              >
                <template v-slot:item.status="{ item }">
                  <v-btn
                    x-small
                    rounded
                    min-width="100"
                    depressed
                    height="32"
                    :color="importResultStatusBtnColor(item.statusKey)"
                    :class="importResultStatusBtnClass(item.statusKey)"
                  >
                    {{ importResultStatusLabel(item.statusKey) }}
                  </v-btn>
                </template>
                <template v-slot:item.actions="{ item }">
                  <div class="import-result-actions">
                    <v-tooltip bottom v-if="item.statusKey === 'Failed'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          fab
                          v-bind="attrs"
                          v-on="on"
                          class="mx-1"
                          color="primary"
                          outlined
                          @click="retryImportFromResult"
                        >
                          <v-icon small>mdi-refresh</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('carbonCredit.plotTreeMapping.imports.retryImportTooltip') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="item.statusKey === 'Success' && item.viewRequestId">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          fab
                          v-bind="attrs"
                          v-on="on"
                          class="mx-1"
                          color="primary"
                          outlined
                          @click="viewRequestFromImportResult(item)"
                        >
                          <v-icon small>mdi-eye-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('carbonCredit.plotTreeMapping.imports.viewRequestTooltip') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          fab
                          v-bind="attrs"
                          v-on="on"
                          class="mx-1"
                          color="error"
                          outlined
                          @click="removeImportResultRow(item.rowKey)"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('carbonCredit.plotTreeMapping.imports.removeFromListTooltip') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </div>
            <div v-else class="py-8 px-4 grey--text text--darken-1 body-2">
              {{ $t('carbonCredit.plotTreeMapping.imports.emptyDataRows') }}
            </div>
          </div>
        </v-card>

        <v-divider class="my-2" />
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn color="primary" depressed class="px-6" @click="closeImportResultDialog">
            {{ $t('continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Papa from "papaparse";
import * as XLSX from "xlsx";
import CarbonCreditService from "@/_services/CarbonCreditService";
import loadingMixin from "@/mixins/LoadingMixin";

const IMPORT_HEADER_EXPECTED_DISPLAY = "ID, Original located, Real location, Comments";

function normalizeImportHeader(h) {
  return String(h || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

const IMPORT_REQUIRED_HEADERS_NORMALIZED = ["id", "real location"];

function importHeadersHaveRequired(headerRow) {
  const set = new Set();
  (headerRow || []).forEach((cell) => {
    const n = normalizeImportHeader(cell);
    if (n) set.add(n);
  });
  return IMPORT_REQUIRED_HEADERS_NORMALIZED.every((req) => set.has(req));
}

function canonicalizeImportRow(raw) {
  const out = {};
  Object.keys(raw || {}).forEach((k) => {
    const nk = normalizeImportHeader(k);
    if (nk) out[nk] = raw[k];
  });
  return out;
}

export default {
  name: "ImportDialog",
  mixins: [loadingMixin],
  props: {
    /** Called after a successful API import, before the result dialog opens (e.g. refresh parent list). */
    afterSuccess: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      importAuditedDialog: false,
      importAuditedDragging: false,
      importAuditedFileName: "",
      importAuditedRows: [],
      importAuditedFileError: null,
      importAuditedSubmitting: false,
      importAuditedResultDialog: false,
      importResultRows: [],
    };
  },
  computed: {
    canSubmitAuditedImport() {
      return (
        !!this.importAuditedFileName &&
        Array.isArray(this.importAuditedRows) &&
        this.importAuditedRows.length > 0 &&
        !this.importAuditedFileError &&
        !this.importAuditedSubmitting
      );
    },
    importResultProgress() {
      return 100;
    },
    importResultProgressColor() {
      if (!this.importResultTotalCount) return "grey";
      if (this.importResultFailCount > 0) return this.importResultSuccessCount > 0 ? "warning" : "error";
      if (this.importResultSkippedCount > 0 && this.importResultSuccessCount === 0) return "warning";
      return "success";
    },
    importResultTotalCount() {
      return this.importResultRows.length;
    },
    importResultSuccessCount() {
      return this.importResultRows.filter((r) => r.statusKey === "Success").length;
    },
    importResultFailCount() {
      return this.importResultRows.filter((r) => r.statusKey === "Failed").length;
    },
    importResultSkippedCount() {
      return this.importResultRows.filter((r) => r.statusKey === "Skipped").length;
    },
    importResultHeaders() {
      return [
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colSpreadsheetId"),
          value: "spreadsheetId",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colRequestId"),
          value: "requestId",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colPlotNo"),
          value: "plotNo",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colLocation"),
          value: "detail",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colStatus"),
          value: "status",
          align: "center",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("carbonCredit.plotTreeMapping.imports.colAction"),
          value: "actions",
          align: "center",
          width: "112px",
          class: "black--text",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    open() {
      this.closeImportResultDialog();
      this.importAuditedDialog = true;
    },
    closeImportResultDialog() {
      this.importAuditedResultDialog = false;
      this.importResultRows = [];
    },
    removeImportResultRow(rowKey) {
      this.importResultRows = this.importResultRows.filter((r) => r.rowKey !== rowKey);
    },
    retryImportFromResult() {
      this.closeImportResultDialog();
      this.open();
    },
    viewRequestFromImportResult(item) {
      if (!item || !item.viewRequestId) return;
      this.closeImportResultDialog();
      this.$router.push({ name: "viewPlotTreeMapping", params: { id: item.viewRequestId } });
    },
    importResultStatusLabel(statusKey) {
      if (statusKey === "Success") return this.$t("carbonCredit.plotTreeMapping.imports.statusSuccess");
      if (statusKey === "Failed") return this.$t("carbonCredit.plotTreeMapping.imports.statusFailed");
      return this.$t("carbonCredit.plotTreeMapping.imports.statusSkipped");
    },
    importResultStatusBtnColor(statusKey) {
      if (statusKey === "Success") return "green2";
      if (statusKey === "Failed") return "red2";
      return "grey lighten-2";
    },
    importResultStatusBtnClass(statusKey) {
      if (statusKey === "Success") return "primary--text";
      if (statusKey === "Failed") return "red--text";
      return "grey--text text--darken-2";
    },
    buildImportResultRowsFromApi(result) {
      const rows = [];
      let n = 0;
      const nextKey = () => `plot-import-${++n}`;
      (result.created || []).forEach((c) => {
        const lat = c.latitude != null ? Number(c.latitude) : NaN;
        const lng = c.longitude != null ? Number(c.longitude) : NaN;
        const loc =
          Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "—";
        rows.push({
          rowKey: nextKey(),
          spreadsheetId: c.spreadsheetId != null ? c.spreadsheetId : "—",
          requestId: c.request_id || "—",
          plotNo: c.plot_no || "—",
          detail: loc,
          statusKey: "Success",
          viewRequestId: c.tree_mapping_request_id,
        });
      });
      (result.errors || []).forEach((e) => {
        rows.push({
          rowKey: nextKey(),
          spreadsheetId: e.spreadsheetId != null ? e.spreadsheetId : "—",
          requestId: "—",
          plotNo: "—",
          detail: e.error || "—",
          statusKey: "Failed",
          viewRequestId: null,
        });
      });
      (result.skipped || []).forEach((s) => {
        rows.push({
          rowKey: nextKey(),
          spreadsheetId: s.spreadsheetId != null ? s.spreadsheetId : "—",
          requestId: "—",
          plotNo: "—",
          detail: s.reason || "—",
          statusKey: "Skipped",
          viewRequestId: null,
        });
      });
      return rows;
    },
    closeImportAuditedDialog() {
      this.importAuditedDialog = false;
      this.importAuditedDragging = false;
      this.importAuditedFileName = "";
      this.importAuditedRows = [];
      this.importAuditedFileError = null;
    },
    clearImportAuditedFile() {
      this.importAuditedFileName = "";
      this.importAuditedRows = [];
      this.importAuditedFileError = null;
    },
    parseSpreadsheetIdForImport(val) {
      if (val === "" || val == null) return null;
      const n = typeof val === "number" && Number.isFinite(val) ? Math.trunc(val) : parseInt(String(val).trim(), 10);
      return Number.isNaN(n) ? null : n;
    },
    async parseAuditedImportFile(file) {
      const name = (file && file.name) || "";
      const ext = name.split(".").pop().toLowerCase();
      if (!["csv", "xls", "xlsx"].includes(ext)) {
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.invalidFileFormat");
        this.importAuditedRows = [];
        return;
      }

      let headerRow;
      let rawObjects = [];

      if (ext === "csv") {
        const text = await file.text();
        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h).replace(/^\uFEFF/, "").trim(),
        });
        headerRow = parsed.meta.fields || [];
        rawObjects = (parsed.data || []).filter((row) =>
          Object.values(row).some((v) => v !== "" && v != null && String(v).trim() !== "")
        );
      } else {
        const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        if (!aoa.length) {
          this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.invalidHeaders");
          this.importAuditedRows = [];
          return;
        }
        headerRow = aoa[0].map((h) => String(h).replace(/^\uFEFF/, "").trim());
        rawObjects = aoa
          .slice(1)
          .map((cols) => {
            const o = {};
            headerRow.forEach((h, i) => {
              o[h] = cols[i] !== undefined ? cols[i] : "";
            });
            return o;
          })
          .filter((row) =>
            Object.values(row).some((v) => v !== "" && v != null && String(v).trim() !== "")
          );
      }

      if (!importHeadersHaveRequired(headerRow)) {
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.headerMismatch", {
          expected: IMPORT_HEADER_EXPECTED_DISPLAY,
        });
        this.importAuditedRows = [];
        return;
      }

      const rows = [];
      const invalidIdLines = [];
      rawObjects.forEach((raw, idx) => {
        const c = canonicalizeImportRow(raw);
        const realRaw = c["real location"];
        const realStr = realRaw != null && String(realRaw).trim() ? String(realRaw).trim() : "";
        if (!realStr) {
          return;
        }
        const sid = this.parseSpreadsheetIdForImport(c.id);
        if (sid == null) {
          invalidIdLines.push(idx + 2);
          return;
        }
        const out = { spreadsheetId: sid, realLocationDms: realStr };
        const notesRaw = c.comments;
        const notes = notesRaw != null && String(notesRaw).trim() ? String(notesRaw).trim() : "";
        if (notes) out.notes = notes;
        rows.push(out);
      });

      if (invalidIdLines.length) {
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.rowInvalidId", {
          rows: invalidIdLines.join(", "),
        });
        this.importAuditedRows = [];
        return;
      }

      if (!rows.length) {
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.noImportableRows");
        this.importAuditedRows = [];
        return;
      }

      this.importAuditedRows = rows;
      this.importAuditedFileError = null;
    },
    async handleImportAuditedFileChange(event) {
      this.importAuditedDragging = false;
      const files = event.target?.files;
      const file = files && files[0];
      if (!file) return;
      this.importAuditedFileName = file.name;
      try {
        await this.parseAuditedImportFile(file);
      } catch (e) {
        console.error(e);
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.serverError");
        this.importAuditedRows = [];
      }
      if (event.target) event.target.value = "";
    },
    async onImportAuditedDrop(event) {
      this.importAuditedDragging = false;
      const file = event.dataTransfer?.files && event.dataTransfer.files[0];
      if (!file) return;
      this.importAuditedFileName = file.name;
      try {
        await this.parseAuditedImportFile(file);
      } catch (e) {
        console.error(e);
        this.importAuditedFileError = this.$t("carbonCredit.plotTreeMapping.imports.serverError");
        this.importAuditedRows = [];
      }
    },
    async submitAuditedPlotImport() {
      if (!this.canSubmitAuditedImport) return;
      this.importAuditedSubmitting = true;
      this.startLoading();
      try {
        const response = await CarbonCreditService.importAuditedPlotsFromSheet({
          rows: this.importAuditedRows,
        });
        if (response.success) {
          const result = response.data || {};
          const summary = result.summary || {};
          const errCount = summary.errors != null ? summary.errors : (result.errors && result.errors.length) || 0;

          if (typeof this.afterSuccess === "function") {
            await this.afterSuccess();
          }
          this.$emit("imported", result);

          this.importResultRows = this.buildImportResultRowsFromApi(result);
          this.importAuditedResultDialog = true;
          this.closeImportAuditedDialog();

          if (errCount > 0) {
            this.$notify({
              type: "warning",
              text: this.$t("carbonCredit.plotTreeMapping.imports.rowErrorsSummary", { count: errCount }),
            });
          } else {
            this.$notify({
              type: "success",
              text: this.$t("carbonCredit.plotTreeMapping.imports.success"),
            });
          }
        } else {
          this.$notify({
            type: "error",
            text: response.message || this.$t("carbonCredit.plotTreeMapping.imports.serverError"),
          });
        }
      } catch (error) {
        console.error(error);
        const msg =
          (error && error.data && error.data.message) ||
          (error && error.data && error.data.msg) ||
          this.$t("carbonCredit.plotTreeMapping.imports.serverError");
        this.$notify({ type: "error", text: msg });
      } finally {
        this.importAuditedSubmitting = false;
        this.stopLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropzone-import {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.dropzone-import__label {
  cursor: pointer;
  width: 100%;
  min-height: 160px;
}

.dropzone-import__label:hover,
.dropzone-import__label--active {
  background-color: #f5f5f5;
}

.dropzone-import__selected {
  min-height: 120px;
  justify-content: center;
}

.plot-import-result-muted {
  font-size: 14px;
  color: #6b7280;
}

.plot-import-result-stats {
  background-color: #ececec;
  border-radius: 4px;
  margin-left: 8px;
  margin-right: 8px;
}

.plot-import-result-stat-num {
  font-size: 25px;
  font-weight: 800;
}

.plot-import-result-stat-total {
  color: #6b7280;
}

.plot-import-result-stat-fail {
  color: #f03737;
}

.plot-import-result-stat-success {
  color: #00bd73;
}

.plot-import-result-stat-label {
  font-size: 13px;
  color: #4b5563;
}

.plot-import-result-table {
  ::v-deep thead tr th {
    background-color: #00bd73 !important;
    color: #fff !important;
    font-weight: 600;
    border-bottom: none !important;
  }

  ::v-deep tbody tr:hover {
    background-color: #f9fafb !important;
  }

  /* Action buttons in one row (v-tooltip wrappers default to block in Vuetify 2) */
  ::v-deep .import-result-actions {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  ::v-deep .import-result-actions > * {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
  }
}

.import-result-actions {
  min-height: 40px;
}

.plot-import-result-progress {
  min-width: 0;
}
</style>
